import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DateValue } from '@internationalized/date';
import { IoMdTime } from 'react-icons/io';
import { TmdxDatepicker, TmdxSelect } from '@ds';
import { BaseCommercialAviaLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useConnectedLegDetails } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { renderSelectItem } from '@/utils/helpers';

type ConnectedLegDetailsProps = {
  type: 'departureLocation' | 'arrivalLocation';
  index: number;
};

export const ConnectedLegDetails = ({ type, index }: ConnectedLegDetailsProps) => {
  const { control } = useFormContext<BaseCommercialAviaLegSchemaValues>();
  const [connectedLegs] = useWatch({ control, name: ['connectedLegs'] });
  const {
    airportsList,
    isAirportsListLoading,
    onAirportSearch,
    onAirportListOpenChange,
    airportsScrollerRef,
    selectedAirport,
    onAirportSelectChange,
    searchValue,
    onDateTimeChange,
  } = useConnectedLegDetails({
    type,
    index,
    defaultId: connectedLegs[index][type].id,
    defaultValue: connectedLegs[index][type].displayName,
  });

  const handleDateTimeChange = (date: DateValue | null) => {
    if (selectedAirport?.location.timezone && date) {
      onDateTimeChange(date, selectedAirport.location.timezone);
    }
  };

  const dateType = type === 'departureLocation' ? 'departureDateTimeValue' : 'arrivalDateTimeValue';

  return (
    <div
      className="grid gap-4 w-full"
      style={{
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr',
      }}
    >
      <Controller
        name={`connectedLegs.${index}.${type}.id`}
        control={control}
        render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid } }) => (
          <TmdxSelect
            label={type === 'departureLocation' ? 'Departure Airport *' : 'Arrival Airport *'}
            placeholder="Choose an option"
            aria-label={type}
            scrollRef={airportsScrollerRef}
            items={airportsList}
            isLoading={isAirportsListLoading}
            isInvalid={invalid}
            isDisabled
            disallowEmptySelection
            onOpenChange={onAirportListOpenChange}
            selectedKeys={value ? [value] : []}
            disabledKeys={value ? [value] : []}
            onSelectionChange={(key) => onAirportSelectChange(key.currentKey as string)}
            onSearchValue={onAirportSearch}
            searchValue={searchValue}
            {...field}
          >
            {renderSelectItem}
          </TmdxSelect>
        )}
      />
      <Controller
        name={`connectedLegs.${index}.${dateType}`}
        control={control}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
          <TmdxDatepicker
            size="md"
            aria-label={`required${type}Time`}
            label={type === 'departureLocation' ? 'Departure Time *' : 'Arrival Time *'}
            hideTimeZone
            isDisabled
            hourCycle={24}
            showMonthAndYearPickers={false}
            granularity="minute"
            value={value}
            onChange={handleDateTimeChange}
            isInvalid={invalid}
            {...field}
            endContent={
              <div className="pointer-events-none flex items-center">
                <span className="text-default-400 text-small">
                  <IoMdTime size={16} />
                </span>
              </div>
            }
          />
        )}
      />
    </div>
  );
};
